import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Executive } from '../api/contract'
import { useShare, toShareTarget } from './ShareModal'
import './RecommendedExecutives.css'
import { InfoTip } from './InfoTip'

/**
 * Local-only shortlist toggle.
 *
 * There is no shortlist endpoint — `counts.shortlisted` exists in the contract
 * but nothing writes it, and neither backend enum has a shortlist state. This
 * gives the button honest feedback for the demo without implying persistence;
 * the state resets on reload, by design rather than by accident.
 */
function ShortlistButton() {
  const [on, setOn] = useState(false)
  return (
    <button
      className="btn" type="button" aria-pressed={on}
      onClick={() => setOn((v) => !v)}
      style={on ? { borderColor: 'var(--teal)', color: 'var(--teal-txt)' } : undefined}
    >
      {on ? 'Shortlisted ✓' : 'Shortlist'}
    </button>
  )
}

function ChatIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
      <path d="M3.5 5.5A1.5 1.5 0 0 1 5 4h10a1.5 1.5 0 0 1 1.5 1.5v6A1.5 1.5 0 0 1 15 13H8l-4.5 3.2z" />
    </svg>
  )
}

function Face({ size }: { size: 'md' | 'lg' }) {
  const px = size === 'lg' ? 52 : 26
  return (
    <div className={size === 'lg' ? 'face lg' : 'face'} aria-hidden="true">
      <svg width={px} height={px} viewBox="0 0 20 20" fill="currentColor">
        <circle cx="10" cy="6.8" r="3.4" />
        <path d="M3.6 17.5c0-3.5 2.9-6.1 6.4-6.1s6.4 2.6 6.4 6.1z" />
      </svg>
    </div>
  )
}

function Factors({ exec, bare = false }: { exec: Executive; bare?: boolean }) {
  return (
    <div className="rows" style={bare ? { border: 0, paddingTop: 10 } : undefined}>
      {exec.match_factors.map((f) => (
        <div className="rw" key={f.label}><span>{f.label}</span><span>{f.value}</span></div>
      ))}
    </div>
  )
}

function ExecutiveCard({ exec, onSelect }: { exec: Executive; onSelect: () => void }) {
  const share = useShare()
  return (
    <article className="card ex">
      <span className="mt tnum">{exec.match_score}% Match</span>
      <div className="ex-t">
        <Face size="md" />
        <div style={{ minWidth: 0 }}>
          <div className="ex-n">{exec.display_name}</div>
          <div className="ex-i">Profile ID: {exec.reference_code}</div>
        </div>
      </div>
      <Factors exec={exec} />
      {/* only rendered when the server actually explains the match */}
      {exec.why_matched && (
        <div>
          <div className="wm-k">Why Matched</div>
          <p className="wm-p">{exec.why_matched}</p>
        </div>
      )}
      <div className="ex-vp">
        <button className="btn" type="button" onClick={onSelect}>View Profile</button>
      </div>
      <div className="ex-b">
        <ShortlistButton />
        <button className="btn" type="button" onClick={() => share(toShareTarget(exec))}>Share</button>
        <Link className="btn chatbtn" to="/conversations"><ChatIcon />Chat</Link>
      </div>
    </article>
  )
}

function CandidatePanel({ exec, index, total, onStep }: {
  exec: Executive; index: number; total: number; onStep: (delta: number) => void
}) {
  const share = useShare()
  return (
    <div className="card ov ov-cand" aria-label="Candidate preview">
      <div className="ov-h">
        <span className="ov-t">Candidate Preview</span>
        <div className="pg" style={{ marginLeft: 'auto' }}>
          <button type="button" aria-label="Previous" onClick={() => onStep(-1)}>
            <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 4 6 10l6 6" /></svg>
          </button>
          <span className="tnum">{index + 1} / {total}</span>
          <button type="button" aria-label="Next" onClick={() => onStep(1)}>
            <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="m8 4 6 6-6 6" /></svg>
          </button>
        </div>
      </div>
      <div className="ov-b">
        <span className="mt tnum" style={{ display: 'inline-block', margin: '0 0 10px 96px' }}>
          {exec.match_score}% Match
        </span>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <Face size="lg" />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{exec.display_name}</div>
            <div className="ex-i">Profile ID: {exec.reference_code}</div>
            <Factors exec={exec} bare />
          </div>
        </div>
        {exec.why_matched && (
          <div style={{ marginTop: 12 }}>
            <div className="wm-k">Why Matched</div>
            <p className="wm-p">{exec.why_matched}</p>
          </div>
        )}
        {exec.core_strengths.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="wm-k">Core Strengths</div>
            <div className="str">
              {exec.core_strengths.map((s) => <span className="tg" key={s}>{s}</span>)}
            </div>
          </div>
        )}
        <Link className="btn btn-teal btn-b" to="/recommended" style={{ marginTop: 14 }}>View Full Profile</Link>
        <div className="ex-b" style={{ marginTop: 8 }}>
          <ShortlistButton />
          <button className="btn" type="button" onClick={() => share(toShareTarget(exec))}>Share</button>
          <Link className="btn chatbtn" to="/conversations"><ChatIcon />Start Conversation</Link>
        </div>
      </div>
    </div>
  )
}

export function RecommendedExecutives() {
  const [selected, setSelected] = useState(0)
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['executives', 'recommended'],
    queryFn: () => api.getRecommendedExecutives(),
  })

  const items = data?.items ?? []
  // clamp rather than trust the index: the list can shrink between renders
  const current = items[Math.min(selected, Math.max(items.length - 1, 0))]

  return (
    <section className="card sec" style={{ padding: '18px 20px' }}>
      <h2 className="h2">Recommended Executives <InfoTip tip="recommended" /></h2>
      <p className="sub" style={{ marginBottom: 14 }}>
        AI-curated confidential executives with strong alignment to your open roles
      </p>

      {isError && (
        <div className="rc-msg" role="alert">
          <b>Could not load recommended executives</b>
          <button className="btn" type="button" onClick={() => void refetch()} style={{ marginTop: 10 }}>Try again</button>
        </div>
      )}

      {data && items.length === 0 && (
        <div className="rc-msg">
          <b>No recommendations yet</b>
          Once a role pipeline is open, matching executives appear here.
        </div>
      )}

      {!isError && (items.length > 0 || isPending) && (
      <div className="rexpair">
        <div className="execs" aria-busy={isPending}>
          {isPending && [0, 1, 2].map((i) => (
            <div className="card ex rc-skeleton" key={i} style={{ minHeight: 300 }} />
          ))}
          {items.map((exec, i) => (
            <ExecutiveCard key={exec.id} exec={exec} onSelect={() => setSelected(i)} />
          ))}
        </div>
        {current && (
          <CandidatePanel
            exec={current}
            index={Math.min(selected, items.length - 1)}
            total={items.length}
            onStep={(d) => setSelected((s) => (s + d + items.length) % items.length)}
          />
        )}
      </div>
      )}
    </section>
  )
}
